/**
 * MediaPipe-based detection for "Làm bài kết hợp vận động".
 * Head: chỉ nghiêng trái/phải (lệch ngang mũi–mắt, không phân loại khuôn mặt). Pose / Hand: như cũ.
 */
/** Landmark tối thiểu (MediaPipe normalized) — tránh import static mediapipe vào bundle chính. */
type LM = { x: number; y: number; z?: number };

export type MovementTrackMode = 'head' | 'pose' | 'hand';

export type Direction4 = 'up' | 'down' | 'left' | 'right';

/** Dáng người (MCQ 4 phương án): A–D theo thứ tự dưới đây */
export type PoseBody4 = 'left_arm_out' | 'right_arm_out' | 'arms_up' | 'arms_crossed';

const WASM_VER = '0.10.34';
const WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${WASM_VER}/wasm`;

const FACE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const GESTURE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task';
const POSE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

/** Cử chỉ MediaPipe → chỉ số phương án 0..3 (MCQ 4 đáp án) */
export const GESTURE_SLOT_MCQ: Record<string, number> = {
  Thumb_Up: 0,
  Victory: 1,
  Open_Palm: 2,
  Pointing_Up: 3,
};

/** True/False: ngón cái lên / xuống */
export const GESTURE_SLOT_TF: Record<string, number> = {
  Thumb_Up: 0,
  Thumb_Down: 1,
};

/**
 * Chỉ nghiêng đầu trái/phải: lệch ngang mũi so với giữa hai mắt (không dùng ngửa/cúi / “khuôn mặt”).
 * @param sensitivity >1 = nhạy hơn (dễ bắt hơn), <1 = cần nghiêng rõ hơn
 */
function classifyHeadTiltLROnly(
  lm: LM[],
  mirrorSelfieLR = true,
  relaxedLR = false,
  sensitivity = 1
): 'left' | 'right' | null {
  if (!lm?.[1] || !lm[33] || !lm[263]) return null;
  const nose = lm[1];
  const le = lm[33];
  const re = lm[263];
  const eyeCx = (le.x + re.x) / 2;
  const iw = Math.hypot(re.x - le.x, re.y - le.y) || 1e-6;
  const dx = (nose.x - eyeCx) / iw;
  const base = relaxedLR ? 0.056 : 0.095;
  const s = Math.max(0.35, Math.min(2.75, sensitivity));
  const TH = base / s;
  if (Math.abs(dx) < TH) return null;
  if (mirrorSelfieLR) return dx > 0 ? 'left' : 'right';
  return dx > 0 ? 'right' : 'left';
}

/**
 * Nhận diện dáng người 4 tư thế (MediaPipe Pose):
 * giang tay trái / giang tay phải / giơ tay lên cao / khoanh tay trước ngực.
 */
function classifyPoseBody4(lm: LM[], relaxedArmExt = false): PoseBody4 | null {
  if (!lm || lm.length < 17) return null;
  const ls = lm[11];
  const rs = lm[12];
  const lw = lm[15];
  const rw = lm[16];
  const shW = Math.abs(rs.x - ls.x) || 0.12;
  const midX = (ls.x + rs.x) / 2;
  const armExt = relaxedArmExt ? 0.036 : 0.055;

  /** Giơ tay lên cao: cả hai cổ tay cao hơn vai (y nhỏ hơn) */
  if (lw.y < ls.y - 0.035 && rw.y < rs.y - 0.035) return 'arms_up';

  const wristDist = Math.hypot(lw.x - rw.x, lw.y - rw.y);
  /** Khoanh tay: cổ tay gần nhau ở ngực, hoặc tay trái sang phải / phải sang trái */
  const inChestBand =
    lw.y > ls.y - 0.03 &&
    lw.y < ls.y + 0.24 &&
    rw.y > rs.y - 0.03 &&
    rw.y < rs.y + 0.24;
  const wristsCrossedX = lw.x > midX - 0.03 && rw.x < midX + 0.03 && lw.x > rw.x - 0.02;
  if (inChestBand && (wristDist < shW * 0.48 || wristsCrossedX)) return 'arms_crossed';

  const leftExt = lw.x < ls.x - armExt;
  const rightExt = rw.x > rs.x + armExt;
  const horizL = Math.abs(lw.y - ls.y) < 0.16;
  const horizR = Math.abs(rw.y - rs.y) < 0.16;

  if (leftExt && horizL && !(rightExt && horizR)) return 'left_arm_out';
  if (rightExt && horizR && !(leftExt && horizL)) return 'right_arm_out';
  if (leftExt && horizL) return 'left_arm_out';
  if (rightExt && horizR) return 'right_arm_out';

  return null;
}

function poseBodyToSlot(p: PoseBody4): number {
  const map: Record<PoseBody4, number> = {
    left_arm_out: 0,
    right_arm_out: 1,
    arms_up: 2,
    arms_crossed: 3,
  };
  return map[p];
}

/** Đúng/Sai hoặc MCQ 2 đáp án: chỉ giang trái / giang phải */
function classifyPoseSplitLR(lm: LM[], relaxedArmExt = false): 'left' | 'right' | null {
  const p = classifyPoseBody4(lm, relaxedArmExt);
  if (p === 'left_arm_out') return 'left';
  if (p === 'right_arm_out') return 'right';
  return null;
}

export type EngineInstance = {
  mode: MovementTrackMode;
  face?: { detectForVideo: (video: HTMLVideoElement, ts: number) => { faceLandmarks?: LM[][] }; close: () => void };
  pose?: { detectForVideo: (video: HTMLVideoElement, ts: number) => { landmarks?: LM[][] }; close: () => void };
  gesture?: {
    recognizeForVideo: (video: HTMLVideoElement, ts: number) => { gestures?: { categoryName?: string }[][] };
    close: () => void;
  };
  close: () => void;
};

export async function createMovementEngine(mode: MovementTrackMode): Promise<EngineInstance> {
  const mp = await import('@mediapipe/tasks-vision');
  const { FilesetResolver, FaceLandmarker, GestureRecognizer, PoseLandmarker } = mp;
  const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
  const tryGpuCpu = async <T>(fn: (d: 'GPU' | 'CPU') => Promise<T>): Promise<T> => {
    try {
      return await fn('GPU');
    } catch {
      return await fn('CPU');
    }
  };

  if (mode === 'head') {
    const face = await tryGpuCpu((delegate) =>
      FaceLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: FACE_MODEL, delegate },
        runningMode: 'VIDEO',
        numFaces: 1,
        minFaceDetectionConfidence: 0.5,
        minFacePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      })
    );
    return {
      mode,
      face,
      close: () => {
        try {
          face.close();
        } catch {
          /* noop */
        }
      },
    };
  }

  if (mode === 'pose') {
    const pose = await tryGpuCpu((delegate) =>
      PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: POSE_MODEL, delegate },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      })
    );
    return {
      mode,
      pose,
      close: () => {
        try {
          pose.close();
        } catch {
          /* noop */
        }
      },
    };
  }

  const gesture = await tryGpuCpu((delegate) =>
    GestureRecognizer.createFromOptions(vision, {
      baseOptions: { modelAssetPath: GESTURE_MODEL, delegate },
      runningMode: 'VIDEO',
      numHands: 1,
      minHandDetectionConfidence: 0.5,
      minHandPresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    })
  );
  return {
    mode,
    gesture,
    close: () => {
      try {
        gesture.close();
      } catch {
        /* noop */
      }
    },
  };
}

export type FrameDetectResult = {
  /** chỉ số phương án 0..n-1 hoặc null */
  slotIndex: number | null;
  /** nhãn debug: hướng / tên cử chỉ */
  rawLabel: string | null;
};

export type DetectFrameOptions = {
  /** Camera trước: true (mặc định). Camera sau: false — nghiêng trái/phải theo ảnh không gương. */
  headMirrorSelfieLR?: boolean;
  /** Ngưỡng nghiêng: nghiêng đầu nhẹ hơn là đủ */
  headRelaxedLR?: boolean;
  /** Độ nhạy nghiêng đầu (mặc định 1). Càng lớn càng dễ nhận (giáo viên chỉnh). */
  headSensitivity?: number;
};

export function detectFrame(
  engine: EngineInstance,
  video: HTMLVideoElement,
  timestampMs: number,
  optionCount: number,
  isTrueFalse: boolean,
  opts?: DetectFrameOptions
): FrameDetectResult {
  if (!video.videoWidth) return { slotIndex: null, rawLabel: null };
  const headMirrorSelfieLR = opts?.headMirrorSelfieLR !== false;
  const headRelaxedLR = opts?.headRelaxedLR !== false;
  const headSensitivity = opts?.headSensitivity ?? 1;

  if (engine.mode === 'head' && engine.face) {
    const r = engine.face.detectForVideo(video, timestampMs);
    const lm = r.faceLandmarks?.[0];
    if (!lm) return { slotIndex: null, rawLabel: null };
    const dir = classifyHeadTiltLROnly(lm, headMirrorSelfieLR, headRelaxedLR, headSensitivity);
    const rawLabel = dir;
    if (!dir) return { slotIndex: null, rawLabel: null };
    /** Chế đầu: chỉ trái/phải → luôn map slot 0 / 1 (MCQ nhiều đáp án: chỉ A/B bằng cử chỉ) */
    if (isTrueFalse) {
      if (dir === 'left') return { slotIndex: 0, rawLabel: 'left' };
      if (dir === 'right') return { slotIndex: 1, rawLabel: 'right' };
      return { slotIndex: null, rawLabel: null };
    }
    if (optionCount >= 2) {
      if (dir === 'left') return { slotIndex: 0, rawLabel: 'left' };
      if (dir === 'right') return { slotIndex: 1, rawLabel: 'right' };
    }
    return { slotIndex: null, rawLabel: null };
  }

  if (engine.mode === 'pose' && engine.pose) {
    const r = engine.pose.detectForVideo(video, timestampMs);
    const lm = r.landmarks?.[0];
    if (!lm) return { slotIndex: null, rawLabel: null };
    if (isTrueFalse || optionCount === 2) {
      const lr = classifyPoseSplitLR(lm, true);
      if (lr === 'left') return { slotIndex: 0, rawLabel: 'left_arm' };
      if (lr === 'right') return { slotIndex: 1, rawLabel: 'right_arm' };
      return { slotIndex: null, rawLabel: null };
    }
    const body = classifyPoseBody4(lm);
    if (!body) return { slotIndex: null, rawLabel: null };
    const rawLabel = body;
    /** MCQ 3 đáp án: bỏ “khoanh tay”, chỉ nhận 3 tư thế đầu */
    if (optionCount === 3) {
      if (body === 'arms_crossed') return { slotIndex: null, rawLabel: body };
      const slot = poseBodyToSlot(body);
      return { slotIndex: Math.min(slot, 2), rawLabel };
    }
    const slot4 = poseBodyToSlot(body);
    if (optionCount <= 1) return { slotIndex: null, rawLabel };
    return { slotIndex: Math.min(slot4, 3), rawLabel };
  }

  if (engine.mode === 'hand' && engine.gesture) {
    const r = engine.gesture.recognizeForVideo(video, timestampMs);
    const cats = r.gestures?.[0];
    const top = cats?.[0];
    if (!top?.categoryName || top.categoryName === 'None') {
      return { slotIndex: null, rawLabel: null };
    }
    const name = top.categoryName;
    if (isTrueFalse) {
      const si = GESTURE_SLOT_TF[name];
      if (si === undefined) return { slotIndex: null, rawLabel: name };
      return { slotIndex: si < optionCount ? si : null, rawLabel: name };
    }
    const si = GESTURE_SLOT_MCQ[name];
    if (si === undefined) return { slotIndex: null, rawLabel: name };
    if (si >= optionCount) return { slotIndex: null, rawLabel: name };
    return { slotIndex: si, rawLabel: name };
  }

  return { slotIndex: null, rawLabel: null };
}

/** Nhãn hiển thị tiếng Việt cho hướng đầu/dáng */
export const DIRECTION_LABELS: Record<Direction4, string> = {
  up: 'Ngửa mặt / nhìn lên',
  down: 'Cúi mặt / nhìn xuống',
  left: 'Nghiêng trái',
  right: 'Nghiêng phải',
};

export const GESTURE_LABELS_MCQ: string[] = [
  '👍 Ngón cái lên (Thumb up)',
  '✌️ Chữ V (Victory)',
  '🖐️ Bàn tay mở (Open palm)',
  '☝️ Chỉ lên (Pointing up)',
];

export const GESTURE_LABELS_TF: string[] = ['👍 Ngón cái lên → Đúng', '👎 Ngón cái xuống → Sai'];

/** Nhãn gợi ý dáng người (thứ tự A–D khớp poseBodyToSlot) */
export const POSE_BODY_LABELS_MCQ: string[] = [
  'Giang tay trái',
  'Giang tay phải',
  'Giơ tay lên cao',
  'Khoanh tay trước ngực',
];
